import { useState, useEffect, useMemo } from "react";
import { IconTrash } from "@tabler/icons-react";
import {
  Button,
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetFooter,
  Combobox,
  Typography,
  toast,
} from "@altimateai/lego";
import { LoadingButton, Stack } from "@altimateai/lego";
import { TableHeaderType, FilterType, isMultiValueFilter, FilterCondition } from "./types";
import FilterOptionsWithOperator from "./components/FilterOptionsWithOperator";
import SaveFilters from "../tags/SaveFilters";
import { FilterApplicableFor, FilterValue, SavedFilter } from "../tags/types";
import { getFilterOperator } from "./utils/filterConditions";
import useActiveFilters from "./hooks/useActiveFilters";
import { useDatatableContext, FilterConfig } from "./contexts/DatatableContextProvider";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { updateFilter } from "@/helpers/apis";

interface FiltersSectionProps {
  headers: TableHeaderType[];
  onFiltersChange?: (filters: FilterCondition[]) => void;
  onClose?: () => void;
  open?: boolean;
  applicableFor?: FilterApplicableFor;
  editFilter?: SavedFilter;
}

const FiltersSection = ({
  headers,
  onFiltersChange,
  onClose,
  open = false,
  applicableFor,
  editFilter,
}: FiltersSectionProps) => {
  const [filters, setFilters] = useState<FilterCondition[]>([]);
  const [invalidFilterIndices, setInvalidFilterIndices] = useState<number[]>([]);
  const { getActiveFiltersAsConditions, applyFilters } = useActiveFilters(headers);
  const queryClient = useQueryClient();

  // Auto-register filters from header with DatatableContext
  const { registerFilters, state } = useDatatableContext();
  const { mutate, isPending: isLoading } = useMutation({
    mutationFn: (data: SavedFilter) => {
      return updateFilter(data);
    },
    onSuccess: resp => {
      queryClient.invalidateQueries({ queryKey: [`SAVED_FILTERS_${applicableFor}`] });
      const event = new CustomEvent("showFilter", {
        detail: resp?.preset_id,
      });
      document.dispatchEvent(event);
      onClose?.();
      toast({ description: "Filter updated successfully!" });
    },
    onError: (err: unknown) => {
      toast({ description: `Error saving the filter: ${err}`, variant: "destructive" });
    },
  });

  // Memoize header search keys to compare with existing filters
  const headerSearchKeys = useMemo(() => {
    const keys: string[] = [];
    headers
      .filter(header => header.filter?.searchKey)
      .forEach(header => {
        keys.push(header.filter!.searchKey!);
        // Also include filterModeSearchKey if it exists
        if ("filterModeSearchKey" in header.filter! && header.filter!.filterModeSearchKey) {
          keys.push(header.filter!.filterModeSearchKey);
        }
      });
    return keys.sort();
  }, [headers]);

  // Memoize existing filter keys from context state
  const existingFilterKeys = useMemo(() => {
    return Object.keys(state.filters).sort();
  }, [state.filters]);

  const getFilterLabel = (header: TableHeaderType) => {
    if (header.filter?.label) {
      return header.filter?.label;
    }
    if (typeof header.label === "string") return header.label;

    return header.uid;
  };

  useEffect(() => {
    // check if existingFilterKeys contains all headerSearchKeys
    const keysMatch = headerSearchKeys.every(key => existingFilterKeys.includes(key));

    if (!keysMatch) {
      // Convert headers to FilterConfig format
      const filters: Record<string, FilterConfig> = {};
      headers.forEach(col => {
        if (col.filter?.searchKey) {
          filters[col.filter.searchKey] = {
            label: getFilterLabel(col),
            isArray: isMultiValueFilter(col.filter.filterType),
            filterType: col.filter.filterType,
          };

          // Also register filterModeSearchKey if it exists
          if ("filterModeSearchKey" in col.filter && col.filter.filterModeSearchKey) {
            filters[col.filter.filterModeSearchKey] = {
              label: `${getFilterLabel(col)} Mode`,
              isArray: false,
              filterType: FilterType.TEXT,
            };
          }
        }
      });
      registerFilters(filters);
    }
  }, [headerSearchKeys, existingFilterKeys, registerFilters, headers]);

  // Build available fields from headers
  const headerFields = headers
    .filter(header => {
      // Must have a searchKey
      if (!header.filter?.searchKey) return false;

      if (
        header.filter.filterType === FilterType.DROPDOWN &&
        (!header.filter.options || header.filter.options.length === 0) &&
        !header.filter.getOptions
      ) {
        return false;
      }

      return true;
    })
    .map(header => ({
      value: header.filter?.searchKey!,
      label: getFilterLabel(header),
      filterType: header.filter?.filterType || FilterType.TEXT,
      filter: header.filter,
    }));

  // Get search keys already covered by headers
  const headerFieldSearchKeys = new Set(headerFields.map(f => f.value));

  // Build available fields from registered filters in context (for programmatic filters)
  const registeredFields = Object.values(state.filters)
    .filter(f => {
      // Only include filters with a filter configuration that aren't already in headers
      if (!f.filterConfig?.searchKey) return false;
      if (headerFieldSearchKeys.has(f.filterConfig.searchKey)) return false;

      // Skip dropdown filters without options
      if (
        f.filterConfig.filterType === FilterType.DROPDOWN &&
        (!f.filterConfig.options || f.filterConfig.options.length === 0)
      ) {
        return false;
      }

      return true;
    })
    .map(f => ({
      value: f.filterConfig!.searchKey!,
      label: f.label,
      filterType: f.filterConfig!.filterType || FilterType.TEXT,
      filter: f.filterConfig,
    }));

  // Merge header fields with registered fields
  const availableFields = [...headerFields, ...registeredFields];

  // Handle edit mode initialization
  useEffect(() => {
    if (open) {
      // Convert editFilter to FilterCondition format for local state
      if (editFilter) {
        const editFilters = Object.entries(editFilter.data as Record<string, FilterValue>).map(
          ([searchKey, value], index) => ({
            field: searchKey,
            value: Array.isArray(value.values) ? value.values : [value.values],
            condition: value.condition,
            logicalOperator: index > 0 ? ("AND" as "AND" | "OR" | undefined) : undefined,
          })
        );
        setFilters(editFilters);
      } else {
        const contextFilters = getActiveFiltersAsConditions();
        if (contextFilters.length > 0) {
          setFilters(contextFilters);
        } else {
          // Show one empty filter if no context filters exist
          setFilters([
            {
              field: "",
              value: "",
              logicalOperator: undefined,
            },
          ]);
        }
      }
    } else {
      // Clear filters and validation errors when modal closes to ensure fresh state next time
      setFilters([]);
      setInvalidFilterIndices([]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, editFilter]);

  const getFieldOptionsForFilter = (currentFilterIndex: number) => {
    // Get the current filter's field value to always include it in options
    const currentField = filters[currentFilterIndex]?.field;

    // Get list of fields selected in OTHER filters (not the current one)
    const fieldsSelectedInOtherFilters = filters
      .filter((_, i) => i !== currentFilterIndex)
      .map(f => f.field)
      .filter(field => field !== "");

    // Filter out fields that are already selected in other filters
    return availableFields
      .filter(
        field => field.value === currentField || !fieldsSelectedInOtherFilters.includes(field.value)
      )
      .map(field => ({
        value: field.value,
        label: field.label,
      }));
  };

  const addFilter = () => {
    // Validate last filter before adding new one
    if (filters.length > 0) {
      const lastFilter = filters[filters.length - 1];
      const hasValue = Array.isArray(lastFilter.value)
        ? lastFilter.value.length > 0
        : lastFilter.value && lastFilter.value !== "";

      if (lastFilter.field && !hasValue) {
        // Mark the last filter as invalid if it has a field but no value
        setInvalidFilterIndices([filters.length - 1]);
        return;
      }
    }

    // Clear any previous validation errors
    setInvalidFilterIndices([]);

    // Add an empty filter to local state
    const newFilter: FilterCondition = {
      field: "",
      value: "",
      logicalOperator: filters.length > 0 ? "AND" : undefined,
    };
    setFilters([...filters, newFilter]);
  };

  const removeFilterByIndex = (index: number) => {
    // Remove filter from local state
    const newFilters = filters.filter((_, i) => i !== index);
    setFilters(newFilters);
  };

  const changeFilter = (
    index: number,
    field: keyof FilterCondition,
    value: string | string[] | "AND" | "OR"
  ) => {
    // Update local filters only, don't apply to context until "Apply" is clicked
    const newFilters = [...filters];
    newFilters[index] = { ...newFilters[index], [field]: value };

    // Reset value when field changes
    if (field === "field") {
      newFilters[index].value = "";
    }

    setFilters(newFilters);

    // Clear validation error for this specific filter when user makes changes
    if (invalidFilterIndices.includes(index)) {
      setInvalidFilterIndices(prev => prev.filter(i => i !== index));
    }
  };

  const getHeaderBySearchkey = (searchKey: string) => {
    return headers.find(h => h.filter?.searchKey === searchKey);
  };

  // Column-less filters (registered via ``registerFilters``, e.g. the
  // Queries page's User / Workspace entries that live only in the
  // More-Filters drawer) have no table header. Look them up in the
  // context registry so Save Filter can still resolve their label /
  // type — without this fallback they were silently dropped from the
  // save payload, making "Save filter" fail with "At least one filter
  // must be selected" even with an active filter.
  const getRegisteredFilterBySearchKey = (searchKey: string) => {
    return Object.values(state.filters).find(f => f.filterConfig?.searchKey === searchKey);
  };

  const handleApplyFilters = () => {
    // Validate that all filters have both field and value selected
    const invalidIndices: number[] = [];
    filters.forEach((filter, index) => {
      const hasValue = Array.isArray(filter.value)
        ? filter.value.length > 0
        : filter.value && filter.value !== "";

      if (filter.field && !hasValue) {
        invalidIndices.push(index);
      }
    });

    if (invalidIndices.length > 0) {
      setInvalidFilterIndices(invalidIndices);
      return;
    }

    // Clear any previous validation errors
    setInvalidFilterIndices([]);

    // Apply local filters using the hook
    applyFilters(
      Object.values(filters).reduce(
        (acc, f) => {
          acc[f.field] = { values: f.value };
          return acc;
        },
        {} as Record<string, { values: FilterCondition["value"] }>
      )
    );
    onFiltersChange?.(filters);
    onClose?.();
  };

  // Convert FilterCondition array to the format expected by SaveFilters
  const convertFiltersForSave = () => {
    const saveFiltersFormat: Record<
      string,
      {
        label: string;
        searchKey?: string;
        filterStr: string | string[];
        condition: string;
      }
    > = {};

    // Use local filters that have values for save
    const validFilters = filters.filter(
      filter =>
        filter.field &&
        filter.value &&
        (typeof filter.value === "string"
          ? Boolean(filter.value.trim())
          : Array.isArray(filter.value)
            ? filter.value.length > 0
            : Boolean(filter.value.value.trim()))
    );

    validFilters.forEach((filter, index) => {
      const header = getHeaderBySearchkey(filter.field);
      // Fall back to a registered (column-less) filter when there's no
      // header column for this field — otherwise drawer-only filters
      // (User / Workspace) never make it into the save payload.
      const registered = header ? undefined : getRegisteredFilterBySearchKey(filter.field);
      if ((header || registered) && filter.value) {
        const key = `filter_${index}`;
        const filterType =
          header?.filter?.filterType ?? registered?.filterConfig?.filterType ?? FilterType.TEXT;
        const condition = getFilterOperator(
          filterType,
          header?.filter && "filterMode" in header.filter ? header.filter.filterMode : undefined
        );
        // Extract string/array value from possible { mode, value } object
        const filterStr =
          typeof filter.value === "object" && !Array.isArray(filter.value)
            ? filter.value.value
            : filter.value;
        saveFiltersFormat[key] = {
          label: header ? getFilterLabel(header) : (registered?.label ?? filter.field),
          searchKey:
            header?.filter?.searchKey ?? registered?.filterConfig?.searchKey ?? filter.field,
          filterStr,
          condition,
        };
      }
    });

    return saveFiltersFormat;
  };

  const saveUpdatedFilter = () => {
    if (!editFilter) return;
    const convertedData = convertFiltersForSave();
    const updatedFilter: SavedFilter = {
      ...editFilter,
      data: Object.values(convertedData).reduce(
        (acc, curr) => {
          if (curr.searchKey) {
            acc[curr.searchKey] = {
              values: curr.filterStr,
              condition: curr.condition,
            };
          }
          return acc;
        },
        {} as Record<string, FilterValue>
      ),
    };
    mutate(updatedFilter);
  };

  return (
    <Sheet open={open} onOpenChange={onClose}>
      <SheetContent className="!al-max-w-none !al-w-[780px]">
        <Stack direction="column" className="al-h-full" gap="none">
          <SheetHeader>
            <SheetTitle>Add Filters</SheetTitle>
          </SheetHeader>

          <div className="al-py-6 al-flex-1 al-pl-1">
            <div className="al-space-y-4">
              {filters.map((filter, index) => (
                <div key={filter.field} className={`al-py`}>
                  <Stack align="start" gap="sm" wrap>
                    <div className="al-w-12 al-mt-2">
                      {index > 0 ? (
                        <Typography className="al-text-sm al-font-medium al-text-gray-600 al-px-2 al-py-1 al-bg-gray-100 al-rounded">
                          AND
                        </Typography>
                      ) : (
                        <Typography className="al-text-sm">Where</Typography>
                      )}
                    </div>

                    <Combobox
                      options={getFieldOptionsForFilter(index)}
                      value={filter.field}
                      onChange={(value: string) => changeFilter(index, "field", value)}
                      placeholder="Select field"
                      searchPlaceholder="Search fields..."
                      buttonProps={{ className: "!al-w-40", "aria-label": `Select field` }}
                      multiSelect={false}
                      popoverProps={{ defaultOpen: !filters[filters.length - 1].field }}
                    />

                    <div className="al-flex-1 al-min-w-0">
                      {filter.field ? (
                        <FilterOptionsWithOperator
                          filter={filter}
                          headers={headers}
                          availableFields={availableFields}
                          onFilterChange={(field, value) => changeFilter(index, field, value)}
                          hasError={invalidFilterIndices.includes(index)}
                          activeDialogFilters={filters}
                        />
                      ) : null}
                    </div>

                    {index > 0 || (filter.field && filter.value) ? (
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => removeFilterByIndex(index)}
                        className="hover:al-text-red-700"
                      >
                        <IconTrash size={16} />
                      </Button>
                    ) : null}
                  </Stack>
                </div>
              ))}
            </div>
            {!filters.length ? (
              <Typography className="al-text-gray" size="sm">
                No filters added. Click to add new filters.
              </Typography>
            ) : null}

            <div className="al-mt-4">
              <Button variant="outline" onClick={addFilter}>
                + Add Filter
              </Button>
            </div>
          </div>
          <SheetFooter>
            <Stack gap="sm" className="al-w-full" justify="between">
              {!editFilter && applicableFor && filters.some(f => f.field && f.value) && (
                <SaveFilters applicableFor={applicableFor} filters={convertFiltersForSave()} />
              )}
              <Stack justify="end" className="al-flex-1">
                <Button variant="ghost" onClick={onClose}>
                  Cancel
                </Button>
                {editFilter ? (
                  <LoadingButton loading={isLoading} onClick={saveUpdatedFilter}>
                    Save changes
                  </LoadingButton>
                ) : (
                  <Button onClick={handleApplyFilters}>Apply filters</Button>
                )}
              </Stack>
            </Stack>
          </SheetFooter>
        </Stack>
      </SheetContent>
    </Sheet>
  );
};

export default FiltersSection;
